// The category tree, as the client graph reads it.
//
// The wire sends the tree **flat** — one row per category with `parentId` and
// `depth`, in preorder — because a nested response is a `$ref` cycle the drift
// test's face reduction cannot compare. The select that draws it needs the
// nesting back, and needs to know which rows to expand and which to hide while
// the user is typing; those three jobs are this module.
//
// Declared here rather than imported from `@/server/data/internal-api/catalog`:
// the client graph may never import `@/server/*`, type-only imports included
// (`eslint.config.js`, "The client-graph -> server-graph boundary"). The
// resulting two spellings are pinned in
// `@/api/contracts/catalog-wire-drift.unit.test.ts`, the one tier that can see
// both sides — the same arrangement `@/lib/catalog-item` stands in.
//
// Every function here is total: the tree read is derived from `parent_id` on
// each request and can be read mid-edit, so a missing parent or a cycle is an
// ordinary case to survive rather than an invariant to trust.

/** One row of the flat tree read. */
export interface CatalogCategoryNodeInput {
  id: number;
  name: string;
  /** How the browse URL names this category (`?category=<slug>`). */
  slug: string;
  parentId?: number | null;
  depth: number;
}

/** The same row, nested, with its depth recomputed from where it actually sits. */
export interface CatalogCategoryTreeNode {
  id: number;
  name: string;
  slug: string;
  depth: number;
  children: CatalogCategoryTreeNode[];
}

/**
 * The flat read, nested.
 *
 * Two rules keep a partial or contradictory response renderable:
 *
 *   - **A node whose parent is not in the response becomes a root.** The
 *     alternative is a category that vanishes from the menu, which reads as
 *     data loss rather than as the mid-edit read it is.
 *   - **A node inside a parent cycle is dropped.** It cannot be placed under a
 *     root, and following its parents to find out would not terminate. Dropping
 *     is the only answer that renders at all; `depth` is not trusted to
 *     disambiguate, because it comes from the same broken parentage.
 *
 * `depth` on the result is recomputed from the nesting rather than carried
 * over, so an orphan promoted to a root reads as depth 0 — which is where the
 * menu will indent it.
 */
export function buildCategoryTree(
  nodes: readonly CatalogCategoryNodeInput[],
): CatalogCategoryTreeNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const built = new Map<number, CatalogCategoryTreeNode>();
  const roots: CatalogCategoryTreeNode[] = [];

  // Placed in the wire's own order, so siblings keep the server's ordering.
  for (const node of nodes) {
    const parentId = node.parentId ?? null;
    const nested: CatalogCategoryTreeNode = {
      id: node.id,
      name: node.name,
      slug: node.slug,
      depth: 0,
      children: [],
    };
    built.set(node.id, nested);
    if (parentId === null || !byId.has(parentId)) roots.push(nested);
  }

  for (const node of nodes) {
    const parentId = node.parentId ?? null;
    if (parentId === null) continue;
    const parent = built.get(parentId);
    const child = built.get(node.id);
    if (parent === undefined || child === undefined) continue;
    parent.children.push(child);
  }

  // Depth comes from the walk, which also drops anything a cycle left
  // unreachable from a root: a cycle's members are never pushed as roots (each
  // names a parent the response does have) and are never reached from one.
  const stamp = (node: CatalogCategoryTreeNode, depth: number) => {
    node.depth = depth;
    for (const child of node.children) stamp(child, depth + 1);
  };
  for (const root of roots) stamp(root, 0);

  return roots;
}

/** One category from the flat read, at any depth. */
export function findCategory(
  nodes: readonly CatalogCategoryNodeInput[],
  id: number,
): CatalogCategoryNodeInput | undefined {
  return nodes.find((node) => node.id === id);
}

/**
 * The ids between a category and its root, outermost first.
 *
 * What the menu expands when it opens on a selected category, so the selected
 * row is on screen rather than folded away three levels down. Bounded by the
 * node count so a parent cycle cannot spin here either.
 */
export function categoryAncestorIds(
  nodes: readonly CatalogCategoryNodeInput[],
  id: number,
): number[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const ancestors: number[] = [];
  let current = byId.get(id);

  while (current && ancestors.length <= nodes.length) {
    const parentId = current.parentId ?? null;
    if (parentId === null) break;
    const parent = byId.get(parentId);
    if (parent === undefined) break;
    ancestors.unshift(parent.id);
    current = parent;
  }

  return ancestors;
}

/**
 * The tree narrowed to what the typed term matches.
 *
 * A node survives when its own name matches **or** any descendant's does, so a
 * match is always shown inside the branch it lives in rather than as a
 * context-free row.
 *
 * **A match does not carry its subtree along.** The donor's filter is a
 * server-side query over the term and returns matching categories only
 * (`CategoriesFieldPaginated.tsx:47-52,224-226`); typing `app` there gives
 * `Appliances`, and opening it gives `Kitchen Appliances` and `Laundry
 * Appliances` — both of which match too. A descendant that does not match is
 * never shown. This kept the whole subtree on any hit, so `app` produced
 * `Cooktops`, `Dishwashers`, `Garbage Disposal` and the rest of the
 * appliance leaves: a search that made the list *longer* (#634).
 *
 * An empty or blank term is not a filter — the whole tree comes back.
 */
export function filterCategoryTree(
  roots: readonly CatalogCategoryTreeNode[],
  term: string,
): CatalogCategoryTreeNode[] {
  const needle = term.trim().toLowerCase();
  if (needle === "") return [...roots];

  const narrow = (node: CatalogCategoryTreeNode): CatalogCategoryTreeNode | undefined => {
    // Always recurse, matched or not: the children are filtered on their own
    // names either way, and a matching node is kept for itself rather than
    // for what hangs off it.
    const children = node.children.map(narrow).filter((child) => child !== undefined);
    if (node.name.toLowerCase().includes(needle)) return { ...node, children };
    return children.length === 0 ? undefined : { ...node, children };
  };

  return roots.map(narrow).filter((node) => node !== undefined);
}
